// frontend/src/apps/los/services/losService.ts
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';

/**
 * LOS App Service
 * 
 * Handles orchestrated API calls to the backend LOS app
 * 
 * Orchestrator Endpoint: POST /api/los/orchestrator/
 * Features:
 * - Batch multiple component getter/setter calls in one request
 * - Parallel execution on backend
 * - Single response with all component data
 * - Automatic CSRF token handling
 */

interface OrchestratorRequest {
  components: string[]; // Array of unique component IDs
  [key: string]: any; // Optional data for setter components
}

interface ComponentResponse {
  [componentId: string]: {
    status: 'success' | 'error';
    data?: any;
    message?: string;
  };
}

/**
 * Fetch initial data for a page by calling getter functions for multiple components
 * 
 * Example:
 * Gets data for PersonalInfo page components
 * - LOS_GetIndividualName_011
 * - LOS_GetDateOfBirth_013
 * - LOS_GetGender_015
 */
export async function getLosData(componentIds?: string[]): Promise<{
  individual?: any;
  business?: any;
  loan?: any;
  documents?: any;
}> {
  try {
    // If no specific components provided, use defaults for PersonalInfo page
    const components = componentIds || [
      'LOS_GetIndividualName_011',
      'LOS_GetDateOfBirth_013',
      'LOS_GetGender_015',
    ];

    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      { components },
      { withCredentials: true }
    );

    // Normalize response into app sections
    const result: any = {};
    
    Object.entries(response.data).forEach(([componentId, componentData]) => {
      if (componentData.status === 'success' && componentData.data) {
        // Map component IDs to app sections
        if (componentId.includes('Name')) {
          result.individual = { ...result.individual, ...componentData.data };
        } else if (componentId.includes('DateOfBirth')) {
          result.individual = { ...result.individual, ...componentData.data };
        } else if (componentId.includes('Gender')) {
          result.individual = { ...result.individual, ...componentData.data };
        } else if (componentId === 'LOS_GetBusinessDetails_003') {
          result.business = { ...result.business, ...componentData.data };
        } else if (componentId.includes('Loan')) {
          result.loan = { ...result.loan, ...componentData.data };
        }
      } else if (componentId === 'LOS_GetBusinessDetails_003' && componentData.status === 'error') {
        // Sometimes backend returns 404 for no data, ignore
      }
    });

    return result;
  } catch (error) {
    console.error('❌ Failed to load LOS data:', error);
    throw error;
  }
}

/**
 * Sync pending changes back to backend via setter components
 * 
 * Example:
 * Sets data for modified components
 * - LOS_SetIndividualName_012
 * - LOS_SetDateOfBirth_014
 * - LOS_SetGender_016
 */
/**
 * Generic orchestrator call for batching getter/setter component APIs.
 */
export async function orchestratorBatch(componentIds: string[], payload: any = {}): Promise<Record<string, any>> {
  try {
    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      { 
        components: componentIds,
        ...payload
      },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('❌ Orchestrator batch failure:', error);
    throw error;
  }
}

/**
 * Fetch the current state of the user's journey.
 */
export async function getJourneyState(): Promise<any> {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/journey/state/`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get journey state:', error);
    throw error;
  }
}

/**
 * Log a journey event and optionally advance to the next step.
 */
export async function logJourneyEvent(data: {
  stage?: string | number;
  advance?: boolean;
  route?: string;
  payload?: any;
}): Promise<any> {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/journey/event/`, data, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('❌ Journey event logging failure:', error);
    throw error;
  }
}

/**
 * Sync pending changes back to backend via setter components
 */
export async function syncLosData(pendingData: {
  individual?: any;
  business?: any;
  loan?: any;
  documents?: any;
}): Promise<void> {
  try {
    const components: string[] = [];
    const requestBody: OrchestratorRequest = { components };

    // Determine which setter components to call based on pending data
    if (pendingData.individual) {
      requestBody['LOS_SetIndividualName_012'] = {
        first_name: pendingData.individual.first_name,
        last_name: pendingData.individual.last_name,
      };
      requestBody['LOS_SetDateOfBirth_014'] = {
        date_of_birth: pendingData.individual.date_of_birth,
      };
      requestBody['LOS_SetGender_016'] = {
        gender: pendingData.individual.gender,
      };
      components.push(
        'LOS_SetIndividualName_012',
        'LOS_SetDateOfBirth_014',
        'LOS_SetGender_016'
      );
    }

    if (pendingData.business) {
      // Add business setter components when they exist
      requestBody['LOS_SetBusinessDetails_004'] = pendingData.business;
      components.push('LOS_SetBusinessDetails_004');
    }

    if (pendingData.loan) {
      // Add loan setter components when they exist
      // components.push('LOS_SetLoanAmount_XXX', ...);
    }

    if (components.length === 0) return;

    requestBody.components = components;

    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      requestBody,
      { withCredentials: true }
    );

    // Check for errors in component responses
    Object.entries(response.data).forEach(([componentId, componentData]) => {
      if (componentData.status === 'error') {
        console.error(`❌ ${componentId} failed:`, componentData.message);
      } else {
        console.log(`✅ ${componentId} saved successfully`);
      }
    });
  } catch (error) {
    console.error('❌ Failed to sync LOS data:', error);
    throw error;
  }
}

/**
 * Batch call to get computation results (credit score, loan eligibility, etc.)
 */
export async function getLosComputations(): Promise<{
  creditScore?: any;
  loanEligibility?: any;
  [key: string]: any;
}> {
  try {
    const components = [
      'LOS_CreditScoreCheck_007',
      // Add other computation components
    ];

    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      { components },
      { withCredentials: true }
    );

    const result: any = {};
    
    Object.entries(response.data).forEach(([componentId, componentData]) => {
      if (componentData.status === 'success' && componentData.data) {
        result[componentId] = componentData.data;
      }
    });

    return result;
  } catch (error) {
    console.error('❌ Failed to fetch LOS computations:', error);
    throw error;
  }
}

/**
 * Helper: Call a single component's getter
 */
export async function getComponentData(componentId: string): Promise<any> {
  try {
    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      { components: [componentId] },
      { withCredentials: true }
    );

    const componentData = response.data[componentId];
    if (componentData.status === 'success') {
      return componentData.data;
    } else {
      throw new Error(componentData.message || 'Unknown error');
    }
  } catch (error) {
    console.error(`❌ Failed to fetch ${componentId}:`, error);
    throw error;
  }
}

/**
 * Helper: Call a single component's setter
 */
export async function setComponentData(componentId: string, data: any): Promise<void> {
  try {
    const response = await axios.post<ComponentResponse>(
      `${API_BASE_URL}/api/los/orchestrator/`,
      { components: [componentId], [componentId]: data },
      { withCredentials: true }
    );

    const componentData = response.data[componentId];
    if (componentData.status !== 'success') {
      throw new Error(componentData.message || 'Unknown error');
    }
  } catch (error) {
    console.error(`❌ Failed to set ${componentId}:`, error);
    throw error;
  }
}
